import { UpdatableReferral } from '@getvim/internal-vim-os-sdk/types';
import { EHR_VENDORS } from '../ehrVendors';

export const NOTES_SEPARATOR = '--- Referral details ---';

/**
 * Apply EHR-vendor-specific transformations to the referral writeback payload
 * before it is handed to the generic `buildReferralWriteback` adapter.
 *
 * Practice Fusion specifics (per the PF adapter manifest at
 * `client/ehr-adapters/practice-fusion/src/manifest.ts`):
 *   - Only these referral fields are writable on PF:
 *       targetProvider, basicInformation.specialty, basicInformation.reasons,
 *       conditions.diagnosis (single, refactored path).
 *     Every other field (notes, startDate, endDate, priority, numberOfVisits,
 *     authCode, procedures) is declared non-updatable — sending them produces
 *     SDK warnings ("Application tried to update fields that are not currently
 *     updatable") and clutters the writeback. We strip them.
 *   - PF has no Notes section. The formatted notes block must be appended into
 *     the Reason field instead. We merge it as a trailing entry in `reasons[]`
 *     with a separator header.
 *   - PF accepts max 1 diagnosis per referral. We cap `diagnosis` to the first
 *     entry. Optum enforces this server-side and the PF adapter itself only
 *     writes `diagnosis[0]`, so this is belt-and-suspenders.
 *
 * For non-PF vendors the input is returned unchanged.
 */
export function applyVendorWritebackOverrides(
  referralToUpdate: UpdatableReferral,
  ehrVendor?: string,
): UpdatableReferral {
  if (ehrVendor?.toLowerCase() !== EHR_VENDORS.PRACTICE_FUSION) {
    return referralToUpdate;
  }

  const { basicInformation, conditions, targetProvider } = referralToUpdate;
  const notes = basicInformation?.notes;
  const reasons = basicInformation?.reasons;

  const mergedReasons =
    notes && notes.trim().length > 0 ? [...(reasons ?? []), NOTES_SEPARATOR, notes] : reasons;

  const diagnosis = conditions?.diagnosis;
  // The SDK type is `[T, ...T[]] | undefined` (zod `nonempty`). Pin the
  // tuple shape on the trimmed value so we don't widen it to `T[]`.
  const cappedDiagnosis =
    diagnosis && diagnosis.length > 1
      ? ([diagnosis[0]] as NonNullable<typeof diagnosis>)
      : diagnosis;

  // Whitelist: build a minimal payload containing only PF-updatable fields.
  // Anything else from the original payload is intentionally dropped.
  const next: UpdatableReferral = {};

  if (targetProvider) {
    next.targetProvider = targetProvider;
  }

  const nextBasicInformation: NonNullable<UpdatableReferral['basicInformation']> = {};
  if (basicInformation?.specialty) {
    nextBasicInformation.specialty = basicInformation.specialty;
  }
  if (mergedReasons && mergedReasons.length > 0) {
    nextBasicInformation.reasons = mergedReasons;
  }
  if (Object.keys(nextBasicInformation).length > 0) {
    next.basicInformation = nextBasicInformation;
  }

  if (cappedDiagnosis && cappedDiagnosis.length > 0) {
    next.conditions = { diagnosis: cappedDiagnosis };
  }

  return next;
}
